/**
 * Tech Portal Dashboard
 * Aligned with Customer Portal Design & Dark Theme
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  MdPeople, 
  MdBusinessCenter, 
  MdVpnKey, 
  MdAdminPanelSettings,
  MdBarChart,
  MdSecurity,
  MdPersonAdd
} from 'react-icons/md';
import { cn } from '../lib/utils';
import {
  erpHeadingAccentClass,
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
} from '@euroasiann/shared/ui';

export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [counts, setCounts] = React.useState({
    users: 0,
    organizations: 0,
    licenses: 0,
    adminUsers: 0
  });

  React.useEffect(() => {
    const fetchCounts = async () => {
      try {
        const { apiFetch } = await import('../utils/api');
        const [usersRes, orgsRes, licensesRes] = await Promise.all([
          apiFetch('/api/v1/tech/users'),
          apiFetch('/api/v1/tech/organizations'),
          apiFetch('/api/v1/tech/licenses')
        ]);

        const data = {
          users: 0,
          organizations: 0,
          licenses: 0,
          adminUsers: 0
        };

        if (usersRes.ok) {
          const res = await usersRes.json();
          const users = Array.isArray(res.data) ? res.data : [];
          data.users = users.length;
          data.adminUsers = users.filter((u: any) => u.portalType === 'tech').length;
        }

        if (orgsRes.ok) {
          const res = await orgsRes.json();
          data.organizations = (Array.isArray(res.data) ? res.data : []).length;
        }

        if (licensesRes.ok) {
          const res = await licensesRes.json();
          data.licenses = (Array.isArray(res.data) ? res.data : []).length;
        }

        setCounts(data);
      } catch (err) {
        console.error('Failed to fetch dashboard counts', err);
      }
    };

    fetchCounts();
  }, []);

  const cards = [
    {
      title: 'User Management',
      description: 'Manage platform users & permissions',
      icon: MdPeople,
      path: '/users',
      color: 'text-[#0088cc]',
      bg: 'bg-[#0088cc]/10',
      value: counts.users.toString(),
    },
    {
      title: 'Organizations',
      description: 'Manage enterprise organizations',
      icon: MdBusinessCenter,
      path: '/organizations',
      color: 'text-amber-500',
      bg: 'bg-amber-50 dark:bg-amber-500/10',
      value: counts.organizations.toString(),
    },
    {
      title: 'License Keys',
      description: 'Platform access & entitlement',
      icon: MdVpnKey,
      path: '/licenses',
      color: 'text-purple-500',
      bg: 'bg-purple-50 dark:bg-purple-500/10',
      value: counts.licenses.toString(),
    },
    {
      title: 'Technical Analytics',
      description: 'Platform performance & metrics',
      icon: MdBarChart,
      path: '/analytics',
      color: 'text-emerald-500',
      bg: 'bg-emerald-50 dark:bg-emerald-500/10',
      value: 'Live',
    },
  ];

  return (
    <div className="w-full min-h-screen p-10 bg-[hsl(var(--background))] space-y-12">
      <nav className={erpPageBreadcrumbClass} aria-label="Breadcrumb">
        <span className={erpPageBreadcrumbItemClass}>Technology</span>
        <span className={erpPageBreadcrumbSepClass}>/</span>
        <span className={erpPageBreadcrumbCurrentClass}>Dashboard</span>
      </nav>

      <div className="flex flex-col gap-2">
        <h1 className="text-[28px] font-bold text-[hsl(var(--foreground))] tracking-tight">
          Welcome back, <span className={erpHeadingAccentClass}>{user?.firstName || 'User'}</span>
        </h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] font-medium">
          Manage your platform infrastructure and core data points from this central hub.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              onClick={() => navigate(card.path)}
              className="group relative p-8 bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--border))] shadow-sm hover:border-[#0088cc] hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
            >
              <div className="flex items-center justify-between mb-6">
                <div className={cn(
                  "w-14 h-14 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110",
                  card.bg,
                  card.color
                )}>
                  <Icon className="w-7 h-7" />
                </div>
                <div className="text-right">
                    <span className="block text-2xl font-bold text-[hsl(var(--foreground))] tracking-tight group-hover:text-[#0088cc] transition-colors">{card.value}</span>
                    <span className="text-[10px] font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-widest">Active</span>
                </div>
              </div>
              <h3 className="text-[13px] font-bold text-[hsl(var(--muted-foreground))] mb-1 tracking-tight group-hover:text-[#0088cc] transition-colors uppercase tracking-widest">
                {card.title}
              </h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] font-medium leading-relaxed">
                {card.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Quick Actions / Role Management */}
      <div className="grid grid-cols-1 gap-8">
        <div className="bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--border))] shadow-sm p-8">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-[13px] font-bold text-[hsl(var(--foreground))] tracking-widest uppercase">Platform Control</h2>
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Advanced configuration</p>
                </div>
                <MdAdminPanelSettings className="w-6 h-6 text-[#0088cc]/20" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  onClick={() => navigate('/roles')}
                  className="flex items-center gap-4 p-4 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] hover:border-[#0088cc] transition-all cursor-pointer group"
                >
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--card))] shadow-sm flex items-center justify-center text-[#0088cc]">
                        <MdSecurity className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-[hsl(var(--foreground))] group-hover:text-[#0088cc]">Roles</h4>
                        <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Access control</p>
                    </div>
                </div>
                <div 
                  onClick={() => navigate('/assign-roles')}
                  className="flex items-center gap-4 p-4 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] hover:border-[#0088cc] transition-all cursor-pointer group"
                >
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--card))] shadow-sm flex items-center justify-center text-amber-500">
                        <MdPersonAdd className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-[hsl(var(--foreground))] group-hover:text-amber-500">Assign</h4>
                        <p className="text-[10px] text-[hsl(var(--muted-foreground))]">Role assignments</p>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}
